/** @jsxImportSource @emotion/react */
import React, { Component } from 'react'
import { connect } from 'react-redux'
import { jsx, css } from '@emotion/react'
import AdminMainLayout from './AdminMainLayout'
import { globalSettingsList } from '../../actions/settings'
import { confirmModal } from '../../actions/ui'
import { showSuccess, showError } from '../../actions/Error'
import { adminManualEditableEmailList, adminManualEditableEmailDeleteList } from '../actions/admin_editable_email'
import { adminBulkEmailList } from '../actions/admin_bulk_email'
import { Container, Modal } from 'react-bootstrap'
import { WhiteButton } from '../../components/layout/WhiteButton'
import AdminTableHeader from './AdminTableHeader'
import AdminTableFilter from './AdminTableFilter'
import EditableEmailsTable from './EditableEmailsTable'
import CommonTable from '../../components/CommonTable'
import CardInfoText from '../../components/layout/CardInfoText'
import Timestamp from '../../components/Timestamp'

const run_headers = {
    created_at: { name: 'Run at' },
    status: { name: 'Status' },
    num_sent: { name: 'Sent' },
    num_pending: { name: 'Pending' },
    num_failed: { name: 'Failed' },
}

class AdminManualEditableEmails extends Component {

    constructor(props) {
        super(props)
        this.state = {runs_for_item: null}
    }

    componentDidMount() {
        const { dispatch } = this.props
        // The is_manual filter and the ordering are fixed for this page and set here only.
        // updateListFilter always invalidates the list, so dispatching it from componentDidUpdate
        // would re-render, dispatch again, and loop forever.
        dispatch(adminManualEditableEmailList.updateListFilter({is_manual: true, order_by: '-modified_at'}))
        dispatch(adminManualEditableEmailList.fetchListIfNeeded())
        dispatch(globalSettingsList.ensureGlobalSettingsLoaded())
    }

    componentDidUpdate(prev_props) {
        const { dispatch } = this.props
        dispatch(adminManualEditableEmailList.fetchListIfNeeded())
    }

    onCreate = () => {
        const { history } = this.props
        history.push('/admin/manual_editable_email/')
    }

    onRun = (item) => {
        const { history } = this.props
        history.push(`/admin/editable_email/${item.id}/run`)
    }

    onShowRuns = (item) => {
        const { dispatch } = this.props
        // Scoped to this email, so each row shows only its own runs.
        dispatch(adminBulkEmailList.updateListFilter({editable_email: item.id}))
        dispatch(adminBulkEmailList.fetchListIfNeeded())
        this.setState({runs_for_item: item})
    }

    onHideRuns = () => {
        this.setState({runs_for_item: null})
    }

    getRunCellValue = (header_key, item, index) => {
        switch( header_key ) {
            case 'created_at':
                return <Timestamp value={item.created_at} format='datetime' />
            default:
                return item[header_key]
        }
    }

    onDelete = (item) => {
        const { dispatch } = this.props

        const on_ok = function(json) {
            if ( json.status !== 'success' ) {
                showError("Delete failed", "The email could not be deleted")
                return
            }
            showSuccess("Deleted", "Email deleted")
            dispatch(adminManualEditableEmailList.invalidateList())
            dispatch(adminManualEditableEmailList.fetchListIfNeeded())
        }

        const onConfirmed = () => {
            // Deletes by name, so every language this email was created in goes at once.
            return dispatch(adminManualEditableEmailDeleteList.saveNewObject({name: item.name})).then(on_ok)
        }

        return dispatch(confirmModal({
            heading: 'Delete email',
            text: `Delete "${item.name}"? This deletes the email in every language it was created in, `
                + `not just the ${item.language_code} version shown here. This cannot be undone.`,
            confirm_text: 'Delete',
            onConfirmed: onConfirmed
        }))
    }

    filterEditableEmails = (filter_values) => {
        const { dispatch } = this.props
        dispatch(adminManualEditableEmailList.updateListFilter(filter_values))
    }

    renderRunsModal = () => {
        const that = this
        const { runs_for_item } = this.state
        const { bulk_emails, bulk_emails_loading } = this.props

        return (
            <Modal show={true}
                   onHide={that.onHideRuns}
                   size="lg" >
              <Modal.Header closeButton>
                <Modal.Title>
                  Sends of {runs_for_item.name}
                </Modal.Title>
              </Modal.Header>

              <Modal.Body>
                <CardInfoText>
                  Every run of this email, most recent first. Pending is recipients still to be
                  sent, and Failed has the error with the counts.
                </CardInfoText>
                <CommonTable is_loading={ bulk_emails_loading }
                             empty_message="This email has not been sent yet."
                             headers={ run_headers }
                             items={ bulk_emails }
                             item_list={adminBulkEmailList}
                             getCellValue={ that.getRunCellValue }
                />
              </Modal.Body>
            </Modal>
        )
    }

    render() {
        const { is_loading, can_edit, editable_emails, bulk_emails, bulk_emails_loading } = this.props
        const { runs_for_item } = this.state
        return (
            <AdminMainLayout breadcrumbs={[{name: 'admin_home'},
                                           {name: 'manual_editable_emails', label: 'Manual Emails', url: '/admin/manual_editable_emails'}]}>
              <Container fluid>
                <AdminTableHeader title="Manual Emails">
                  <div css={ css`width:100%;`}>
                    <AdminTableFilter
                        updateOnChange={ this.filterEditableEmails }
                    />
                  </div>

                  { can_edit &&
                    <WhiteButton onClick={this.onCreate}>
                      Create new email
                    </WhiteButton>
                  }

                </AdminTableHeader>
                <EditableEmailsTable
                    is_loading={ is_loading }
                    empty_message={`There are no manual emails.`}
                    items={ editable_emails }
                    item_list={adminManualEditableEmailList}
                    onDelete={ can_edit ? this.onDelete : null }
                    onRun={ can_edit ? this.onRun : null }
                    onShowRuns={ can_edit ? this.onShowRuns : null }
                />
                { runs_for_item && this.renderRunsModal() }
              </Container>
            </AdminMainLayout>
        )
    }

}

function mapStateToProps(state, props) {
    const editable_emails = adminManualEditableEmailList.getVisibleObjects()
    return {
        editable_emails,
        is_loading: adminManualEditableEmailList.isLoading(),
        bulk_emails: adminBulkEmailList.getVisibleObjects() || [],
        bulk_emails_loading: adminBulkEmailList.isLoading(),
        can_edit: globalSettingsList.getSetting('can_edit_editable_emails'),
    }
}

export default connect(mapStateToProps)(AdminManualEditableEmails)
